import { z } from 'zod';
import { DateTimeFormatter, DayOfWeek, Duration, Instant, LocalDate, LocalTime, OffsetDateTime, Period, ZoneId, convert, nativeJs, } from '@js-joda/core';
import { Interval } from '@js-joda/extra';
/**
 * DB-tolerant optional. Accepts the inner value, `null`, or absent (`undefined`)
 * on the wire/DB side; always normalises to `T | undefined` on the JS side.
 *
 * Use this for any DB-stored field whose JS-side semantic is "optional" but
 * whose DB documents may have an explicit `null` (often left over from earlier
 * writes that wrote `null` instead of omitting the field). Pair with the
 * `ignoreUndefined: true` MongoClient option so writes round-trip cleanly:
 * `T | undefined` JS-side → `T` or omitted in BSON, never `null`.
 *
 * @example
 * const Doc = z.object({
 *   description: dbOptional(z.string()),  // DB: string | null | absent → JS: string | undefined
 *   count: dbOptional(z.number()),
 * });
 */
function isCodec(schema) {
    return schema instanceof z.ZodCodec;
}
export function dbOptional(schema) {
    if (isCodec(schema)) {
        return z.codec(z.union([schema.in, z.null()]).optional(), schema.out.optional(), {
            // Decode: collapse explicit `null` (legacy DB writes) into `undefined`; preserve T.
            // Encode: JS-side never produces `null`, only T or `undefined`.
            decode: (val) => (val == null ? undefined : schema.decode(val)),
            encode: (val) => (val === undefined ? undefined : schema.encode(val)),
        });
    }
    else {
        return z.codec(z.union([schema, z.null()]).optional(), schema.optional(), {
            // Decode: collapse explicit `null` (legacy DB writes) into `undefined`; preserve T.
            // Encode: JS-side never produces `null`, only T or `undefined`.
            decode: (val) => (val == null ? undefined : schema.decode(val)),
            encode: (val) => (val === undefined ? undefined : schema.encode(val)),
        });
    }
}
/**
 * Wire format schema for ISO 8601 datetime with timezone offset.
 * Use this when you only need the string validation without codec transformation.
 */
export const InstantStringSchema = z.iso.datetime({ offset: true });
/**
 * Wire format schema for ISO 8601 duration.
 * Use this when you only need the string validation without codec transformation.
 */
export const DurationStringSchema = z.iso.duration();
/**
 * Wire format schema for ISO 8601 period.
 * Use this when you only need the string validation without codec transformation.
 */
export const PeriodStringSchema = z
    .string()
    .regex(/^P(?:\d+Y)?(?:\d+M)?(?:\d+D)?(?:T(?:\d+H|\d+M|\d+(?:\.\d+)?S)(?:\d+H)?(?:\d+M)?(?:\d+(?:\.\d+)?S)?)?$/, {
    message: 'Invalid ISO 8601 period format',
});
/**
 * Custom Zod type for runtime Instant validation.
 * Use this when you need to validate that a value is already an Instant object.
 */
export const ZodInstant = z.custom((val) => val instanceof Instant, {
    message: 'Expected Instant',
});
/**
 * Custom Zod type for runtime LocalDate validation.
 * Use this when you need to validate that a value is already a LocalDate object.
 */
export const ZodLocalDate = z.custom((val) => val instanceof LocalDate, {
    message: 'Expected LocalDate',
});
/**
 * Custom Zod type for runtime LocalTime validation.
 * Use this when you need to validate that a value is already a LocalTime object.
 */
export const ZodLocalTime = z.custom((val) => val instanceof LocalTime, {
    message: 'Expected LocalTime',
});
/**
 * Custom Zod type for runtime Duration validation.
 * Use this when you need to validate that a value is already a Duration object.
 */
export const ZodDuration = z.custom((val) => val instanceof Duration, {
    message: 'Expected Duration',
});
/**
 * Custom Zod type for runtime Period validation.
 * Use this when you need to validate that a value is already a Period object.
 */
export const ZodPeriod = z.custom((val) => val instanceof Period, {
    message: 'Expected Period',
});
/**
 * Custom Zod type for runtime ZoneId validation.
 * Use this when you need to validate that a value is already a ZoneId object.
 */
export const ZodZoneId = z.custom((val) => val instanceof ZoneId, {
    message: 'Expected ZoneId',
});
/**
 * Custom Zod type for runtime DayOfWeek validation.
 * Use this when you need to validate that a value is already a DayOfWeek object.
 */
export const ZodDayOfWeek = z.custom((val) => val instanceof DayOfWeek, {
    message: 'Expected DayOfWeek',
});
/**
 * Custom Zod type for runtime Interval validation.
 * Use this when you need to validate that a value is already an Interval object.
 */
export const ZodInterval = z.custom((val) => val instanceof Interval, {
    message: 'Expected Interval',
});
/**
 * Bidirectional codec for Instant <-> ISO 8601 datetime string.
 *
 * Input: ISO 8601 datetime string (e.g., '2024-01-15T10:30:00Z')
 * Output: js-joda Instant object
 *
 * @example
 * // Decode from string to Instant
 * InstantCodec.decode('2024-01-15T10:30:00Z')  // -> Instant
 *
 * // Encode from Instant to string
 * InstantCodec.encode(instant)  // -> '2024-01-15T10:30:00.000Z'
 *
 * // Parse unknown value (validates, then decodes)
 * InstantCodec.parse('2024-01-15T10:30:00Z')  // -> Instant
 */
export const InstantCodec = z
    .codec(InstantStringSchema, ZodInstant, {
    decode: (isoString) => {
        const odt = OffsetDateTime.parse(isoString, DateTimeFormatter.ISO_OFFSET_DATE_TIME);
        return odt.toInstant();
    },
    encode: (instant) => instant.toString(),
})
    .meta({ id: 'Instant' });
/**
 * Bidirectional codec for Instant <-> JavaScript Date (for MongoDB storage).
 *
 * Input: JavaScript Date object (stored as BSON date in MongoDB)
 * Output: js-joda Instant object
 *
 * Note: JavaScript Date has millisecond precision, so nanosecond data will be lost.
 * Use this codec for MongoDB storage where native BSON dates provide efficient
 * date range queries.
 *
 * @example
 * // Decode from Date to Instant
 * InstantDateCodec.decode(new Date('2024-01-15T10:30:00Z'))  // -> Instant
 *
 * // Encode from Instant to Date
 * InstantDateCodec.encode(instant)  // -> Date
 *
 * // Parse unknown value (validates, then decodes)
 * InstantDateCodec.parse(new Date())  // -> Instant
 */
export const InstantDateCodec = z
    .codec(z.date(), ZodInstant, {
    decode: (date) => nativeJs(date).toInstant(),
    encode: (instant) => convert(instant).toDate(),
})
    .meta({ id: 'InstantDate' });
/**
 * Bidirectional codec for LocalDate <-> ISO 8601 date string.
 *
 * Input: ISO 8601 date string (YYYY-MM-DD format, e.g., '2024-01-15')
 * Output: js-joda LocalDate object
 *
 * @example
 * // Decode from string to LocalDate
 * LocalDateCodec.decode('2024-01-15')  // -> LocalDate
 *
 * // Encode from LocalDate to string
 * LocalDateCodec.encode(localDate)  // -> '2024-01-15'
 *
 * // Parse unknown value (validates, then decodes)
 * LocalDateCodec.parse('2024-01-15')  // -> LocalDate
 */
export const LocalDateCodec = z
    .codec(z.iso.date(), ZodLocalDate, {
    decode: (dateString) => LocalDate.parse(dateString),
    encode: (localDate) => localDate.toString(),
})
    .meta({ id: 'LocalDate' });
/**
 * Bidirectional codec for LocalTime <-> ISO 8601 time string.
 *
 * Input: ISO 8601 time string (HH:mm:ss format, e.g., '10:30:00' or '10:30:00.123')
 * Output: js-joda LocalTime object
 *
 * @example
 * // Decode from string to LocalTime
 * LocalTimeCodec.decode('10:30:00')  // -> LocalTime
 *
 * // Encode from LocalTime to string
 * LocalTimeCodec.encode(localTime)  // -> '10:30:00'
 *
 * // Parse unknown value (validates, then decodes)
 * LocalTimeCodec.parse('10:30:00')  // -> LocalTime
 */
export const LocalTimeCodec = z
    .codec(z.iso.time(), ZodLocalTime, {
    decode: (timeString) => LocalTime.parse(timeString),
    encode: (localTime) => localTime.format(DateTimeFormatter.ISO_TIME),
})
    .meta({ id: 'LocalTime' });
/**
 * Bidirectional codec for Duration <-> ISO 8601 duration string.
 *
 * Input: ISO 8601 duration string (e.g., 'PT1H30M', 'P1D', 'P1DT12H')
 * Output: js-joda Duration object
 *
 * @example
 * // Decode from string to Duration
 * DurationCodec.decode('PT1H30M')  // -> Duration of 1 hour 30 minutes
 *
 * // Encode from Duration to string
 * DurationCodec.encode(duration)  // -> 'PT1H30M'
 *
 * // Parse unknown value (validates, then decodes)
 * DurationCodec.parse('P1D')  // -> Duration of 1 day
 */
export const DurationCodec = z
    .codec(DurationStringSchema, ZodDuration, {
    decode: (durationString) => Duration.parse(durationString),
    encode: (duration) => duration.toString(),
})
    .meta({ id: 'Duration' });
/**
 * Bidirectional codec for Period <-> ISO 8601 period string.
 *
 * Input: ISO 8601 period string (e.g., 'P1Y2M3D', 'P1Y', 'P6M')
 * Output: js-joda Period object
 *
 * @example
 * // Decode from string to Period
 * PeriodCodec.decode('P1Y2M3D')  // -> Period of 1 year, 2 months, 3 days
 *
 * // Encode from Period to string
 * PeriodCodec.encode(period)  // -> 'P1Y2M3D'
 *
 * // Parse unknown value (validates, then decodes)
 * PeriodCodec.parse('P6M')  // -> Period of 6 months
 */
export const PeriodCodec = z
    .codec(PeriodStringSchema, ZodPeriod, {
    decode: (periodString) => Period.parse(periodString),
    encode: (period) => period.toString(),
})
    .meta({ id: 'Period' });
/**
 * Bidirectional codec for ZoneId <-> timezone string.
 *
 * Input: IANA timezone string (e.g., 'America/New_York', 'UTC', 'Europe/London')
 * Output: js-joda ZoneId object
 *
 * @example
 * // Decode from string to ZoneId
 * ZoneIdCodec.decode('America/New_York')  // -> ZoneId
 *
 * // Encode from ZoneId to string
 * ZoneIdCodec.encode(zoneId)  // -> 'America/New_York'
 *
 * // Parse unknown value (validates, then decodes)
 * ZoneIdCodec.parse('Europe/London')  // -> ZoneId
 */
export const ZoneIdCodec = z
    .codec(z.string(), ZodZoneId, {
    decode: (zoneIdString) => ZoneId.of(zoneIdString),
    encode: (zoneId) => zoneId.toString(),
})
    .meta({ id: 'ZoneId' });
/**
 * Bidirectional codec for DayOfWeek <-> day name string.
 *
 * Input: Day of week name (e.g., 'MONDAY', 'TUESDAY', 'SUNDAY')
 * Output: js-joda DayOfWeek object
 *
 * @example
 * // Decode from string to DayOfWeek
 * DayOfWeekCodec.decode('MONDAY')  // -> DayOfWeek.MONDAY
 *
 * // Encode from DayOfWeek to string
 * DayOfWeekCodec.encode(DayOfWeek.MONDAY)  // -> 'MONDAY'
 *
 * // Parse unknown value (validates, then decodes)
 * DayOfWeekCodec.parse('FRIDAY')  // -> DayOfWeek.FRIDAY
 */
const DayOfWeekNames = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
export const DayOfWeekCodec = z
    .codec(z.enum(DayOfWeekNames), ZodDayOfWeek, {
    decode: (dayString) => DayOfWeek.valueOf(dayString),
    encode: (dayOfWeek) => dayOfWeek.name(),
})
    .meta({ id: 'DayOfWeek' });
/**
 * Bidirectional codec for Interval <-> ISO 8601 interval string.
 *
 * Input: ISO 8601 interval string in format '<start>/<end>' (e.g., '2024-01-15T10:30:00Z/2024-01-16T10:30:00Z')
 * Output: js-joda Interval object
 *
 * @example
 * // Decode from string to Interval
 * IntervalCodec.decode('2024-01-15T10:30:00Z/2024-01-16T10:30:00Z')  // -> Interval
 *
 * // Encode from Interval to string
 * IntervalCodec.encode(interval)  // -> '2024-01-15T10:30:00Z/2024-01-16T10:30:00Z'
 *
 * // Parse unknown value (validates, then decodes)
 * IntervalCodec.parse('2024-01-15T10:30:00Z/2024-01-16T10:30:00Z')  // -> Interval
 */
export const IntervalCodec = z
    .codec(z.string(), ZodInterval, {
    decode: (intervalString) => {
        return Interval.parse(intervalString);
    },
    encode: (interval) => {
        return `${interval.start().toString()}/${interval.end().toString()}`;
    },
})
    .meta({ id: 'Interval' });
/**
 * Aliases for backwards compatibility with contracts that use Schema naming
 */
export { InstantCodec as InstantSchema };
export { DurationCodec as DurationSchema };
/**
 * Re-export js-joda types for consistent type resolution across the monorepo.
 * Import these from '@thinkalpha/common/schemas/codecs.js' instead of '@js-joda/core'
 * to avoid TS2742 portability errors in declaration emit.
 */
export { Instant, LocalDate, LocalTime, Duration, Period, ZoneId, DayOfWeek, Interval };
//# sourceMappingURL=codecs.js.map